'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { createClient } from '@/lib/supabase/client';
import LoginForm from '@/components/admin/auth/LoginForm';
import { AuthShell, AuthNotice } from '@/components/admin/auth/AuthShell';

/**
 * Login page content — reads URL params for tenant-scoped login and
 * success/error notices, then renders the standard `AuthShell` chrome
 * around `LoginForm`.
 *
 * Three URL flows:
 *   - `?registered=true`   → "Registrierung erfolgreich" (was email-confirm
 *                           required; for new accounts that already use
 *                           the email-skip flow this banner won't show)
 *   - `?verified=true`     → "Email bestätigt" (post-confirm-link click)
 *   - `?error=…`           → typed error message banner
 *   - `?tenantId=…`        → tenant-scoped login (heading shows tenant name)
 */
export default function AdminLoginContent() {
  const searchParams = useSearchParams();
  const tenantId = searchParams?.get('tenantId');
  const registered = searchParams?.get('registered');
  const verified = searchParams?.get('verified');
  const error = searchParams?.get('error');
  const email = searchParams?.get('email');
  const tAuth = useTranslations('admin.auth');

  const [tenantName, setTenantName] = useState<string | null>(null);

  // Tenant-scoped login → fetch the business name so the heading is
  // personalised ("Anmeldung · Fresh Cut Friseur"). Falls back gracefully
  // if the lookup fails — login still works without the name.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('tenants')
          .select('name')
          .eq('id', tenantId)
          .single<{ name: string }>();
        if (!cancelled && data?.name) setTenantName(data.name);
      } catch {
        /* non-fatal — heading just stays generic */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const errorMessage =
    error === 'auth_callback_failed'
      ? 'Authentifizierung fehlgeschlagen. Bitte erneut versuchen.'
      : error === 'verification_failed'
        ? 'E-Mail-Verifizierung fehlgeschlagen. Der Link ist möglicherweise abgelaufen.'
        : error
          ? 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.'
          : null;

  return (
    <AuthShell
      eyebrow={tenantName ? tenantName : tAuth('secureArea')}
      heading={tAuth('signInHeading')}
      subtitle={
        tenantName
          ? tAuth('loginSubtitleTenant', { tenant: tenantName })
          : tAuth('loginSubtitleAdmin')
      }
      footer={
        <p className="text-center">
          {tAuth('noAccount')}{' '}
          <Link
            href="/register"
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {tAuth('registerNow')}
          </Link>
        </p>
      }
    >
      {/* Stacked notice banners — render conditionally, don't reserve
          vertical space when they're absent. */}
      <div className="space-y-3">
        {registered === 'true' && (
          <AuthNotice tone="success">
            Registrierung erfolgreich! Bitte bestätige deine E-Mail-Adresse vor dem Login.
          </AuthNotice>
        )}
        {verified === 'true' && (
          <AuthNotice tone="success">
            E-Mail-Adresse bestätigt — du kannst dich jetzt anmelden.
          </AuthNotice>
        )}
        {errorMessage && <AuthNotice tone="error">{errorMessage}</AuthNotice>}

        <LoginForm
          tenantId={tenantId || undefined}
          isUnifiedAdmin={!tenantId}
          initialEmail={email || undefined}
        />
      </div>
    </AuthShell>
  );
}
